import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Virtuoso } from 'react-virtuoso';
import { IconCheck } from '@tabler/icons-react';
import {
  Assignment,
  assignmentBelongsToColumn,
  fetchAssignmentsPage,
} from '../../../entities/assignment';

const INITIAL_PAGE_SIZE = 10;
const LOAD_MORE_SIZE = 10;

export type TasksColumnKey = 'overdue' | 'today' | 'tomorrow';

export interface TasksColumnHandle {
  handleRealtimeEvent: (action: string, record: Assignment) => void;
}

export interface AssignmentCardProps {
  assignment: Assignment;
  onCardClick?: (leadId: string) => void;
}

interface TasksColumnProps {
  colKey: TasksColumnKey;
  label: string;
  icon: React.ReactNode;
  cls: string;
  emptyText: string;
  filterUserIds: string[];
  filterMode?: 'any' | 'author' | 'owner';
  onCountChange: (count: number) => void;
  onCardClick?: (leadId: string) => void;
  renderCard: (props: AssignmentCardProps) => React.ReactNode;
}

const TasksColumn = forwardRef<TasksColumnHandle, TasksColumnProps>(
  (
    {
      colKey,
      label,
      icon,
      cls,
      emptyText,
      filterUserIds,
      filterMode,
      onCountChange,
      onCardClick,
      renderCard,
    },
    ref,
  ) => {
    const [items, setItems] = useState<Assignment[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const isLoadingMoreRef = useRef(false);
    const hasMoreRef = useRef(false);
    const pageRef = useRef(1);
    const filterUserIdsRef = useRef(filterUserIds);
    filterUserIdsRef.current = filterUserIds;
    const filterModeRef = useRef(filterMode);
    filterModeRef.current = filterMode;
    const onCountChangeRef = useRef(onCountChange);
    onCountChangeRef.current = onCountChange;

    const loadInitial = useCallback(
      async (userIds: string[], mode?: 'any' | 'author' | 'owner') => {
        setIsLoading(true);
        pageRef.current = 1;
        hasMoreRef.current = false;
        try {
          const { items: newItems, totalItems } = await fetchAssignmentsPage(
            colKey,
            1,
            INITIAL_PAGE_SIZE,
            userIds,
            mode,
          );
          setItems(newItems);
          setTotalCount(totalItems);
          onCountChangeRef.current(totalItems);
          hasMoreRef.current = newItems.length < totalItems;
        } catch (err) {
          console.error(err);
        } finally {
          setIsLoading(false);
        }
      },
      [colKey],
    );

    const filterKey = `${filterUserIds.join(',')}:${filterMode ?? 'any'}`;
    useEffect(() => {
      loadInitial(filterUserIds, filterMode).catch(console.error);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterKey, loadInitial]);

    const handleLoadMore = useCallback(async () => {
      if (isLoadingMoreRef.current || !hasMoreRef.current) return;
      isLoadingMoreRef.current = true;
      setIsLoadingMore(true);
      const nextPage = pageRef.current + 1;
      try {
        const { items: more, totalItems } = await fetchAssignmentsPage(
          colKey,
          nextPage,
          LOAD_MORE_SIZE,
          filterUserIdsRef.current,
          filterModeRef.current,
        );
        setItems(prev => {
          const existingIds = new Set(prev.map(i => i.id));
          const fresh = more.filter(i => !existingIds.has(i.id));
          const merged = [...prev, ...fresh];
          hasMoreRef.current = merged.length < totalItems;
          return merged;
        });
        setTotalCount(totalItems);
        pageRef.current = nextPage;
      } catch (err) {
        console.error(err);
      } finally {
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
      }
    }, [colKey]);

    useImperativeHandle(
      ref,
      () => ({
        handleRealtimeEvent: (action: string, record: Assignment) => {
          const isResolved = record.status === 'COMPLETED';

          if (action === 'delete' || isResolved) {
            setItems(prev => {
              if (!prev.some(i => i.id === record.id)) return prev;
              setTotalCount(c => {
                const next = Math.max(0, c - 1);
                onCountChangeRef.current(next);
                return next;
              });
              return prev.filter(i => i.id !== record.id);
            });
            return;
          }

          const userIds = filterUserIdsRef.current;
          const mode = filterModeRef.current ?? 'any';
          const matchesFilter =
            userIds.length === 0 ||
            (mode === 'author' && userIds.includes(record.authorId)) ||
            (mode === 'owner' && userIds.includes(record.ownerId ?? '')) ||
            (mode === 'any' && (userIds.includes(record.ownerId ?? '') || userIds.includes(record.authorId)));

          const belongsHere =
            assignmentBelongsToColumn(record.planDate, colKey, record.status) && matchesFilter;

          if (belongsHere) {
            if (action === 'create') {
              setItems(prev => {
                if (prev.some(i => i.id === record.id)) return prev;
                setTotalCount(c => {
                  const next = c + 1;
                  onCountChangeRef.current(next);
                  return next;
                });
                return [record, ...prev];
              });
            } else {
              setItems(prev => {
                if (prev.some(i => i.id === record.id)) {
                  return prev.map(i => (i.id === record.id ? record : i));
                }
                setTotalCount(c => {
                  const next = c + 1;
                  onCountChangeRef.current(next);
                  return next;
                });
                return [record, ...prev];
              });
            }
          } else {
            setItems(prev => {
              if (!prev.some(i => i.id === record.id)) return prev;
              setTotalCount(c => {
                const next = Math.max(0, c - 1);
                onCountChangeRef.current(next);
                return next;
              });
              return prev.filter(i => i.id !== record.id);
            });
          }
        },
      }),
      [colKey],
    );

    return (
      <div className={`tasks-col ${cls}`}>
        <div className="tasks-col-header">
          <div className="tasks-col-hi">
            {icon}
            <span className="tasks-col-name">{label}</span>
          </div>
          {totalCount > 0 && <div className="tasks-col-cnt">{totalCount}</div>}
        </div>
        <div className="tasks-col-body">
          {isLoading ? (
            <div className="tasks-col-spinner">
              <div className="tasks-loading-spinner" />
            </div>
          ) : items.length === 0 ? (
            <div className="tasks-col-empty">
              <IconCheck size={20} stroke={1.5} className="tasks-col-empty-icon" />
              <span>{emptyText}</span>
            </div>
          ) : (
            <Virtuoso
              style={{ flex: 1, minHeight: 0 }}
              data={items}
              endReached={handleLoadMore}
              overscan={300}
              itemContent={(_, assignment) => (
                <div style={{ paddingBottom: 7 }}>
                  {renderCard({ assignment, onCardClick })}
                </div>
              )}
              components={{
                Footer: () =>
                  isLoadingMore ? (
                    <div className="tasks-col-spinner" style={{ paddingBottom: 8 }}>
                      <div className="tasks-loading-spinner" />
                    </div>
                  ) : null,
              }}
            />
          )}
        </div>
      </div>
    );
  },
);

TasksColumn.displayName = 'TasksColumn';

export { TasksColumn };
